import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Capacitor } from '@capacitor/core'
import { mapActions } from 'pinia'
import { useNotificationsStore } from 'src/stores/index.js'

// Debe coincidir con @string/default_notification_channel_id del AndroidManifest: ese meta-data
// es el que usa el SDK de FCM para los mensajes que pinta el en segundo plano, y este canal es
// el que usa la app para los que pinta ella en primer plano. Mismo id = misma entrada en los
// ajustes del sistema y mismo comportamiento en los dos casos.
const CHANNEL_ID = 'binnacle_default'

// El drawable monocromatico de res/drawable/. Android exige la silueta; sin esto la
// notificacion local cae al icono de la app aplanado a un circulo blanco.
const SMALL_ICON = 'ic_stat_binnacle'

export const pushNotificationsMixin = {
  methods: {
    ...mapActions(useNotificationsStore, ['markAsRead']),

    // Un mensaje con bloque `notification` solo lo pinta el SDK de FCM cuando la app esta en
    // segundo plano. Con la app abierta el SDK entrega el mensaje al codigo y no muestra nada:
    // hasta ahora eso hacia invisible, por ejemplo, el aviso de "Finalizaste <formulario>", que
    // se dispara justo al cerrar una auditoria, o sea siempre con la app en pantalla.
    // Republicarlo como notificacion local cierra ese hueco sin duplicar nada, porque los dos
    // caminos son mutuamente excluyentes.
    async showForegroundNotification (notification) {
      const title = notification.title || ''
      const body = notification.body || ''
      // Mensaje de solo datos: no hay nada que mostrar.
      if (!title && !body) return

      // Guarda para el caso de solo datos recibido con la app cerrada: el plugin retiene el
      // mensaje y lo entrega al abrir, y sin esto se publicaria un aviso viejo como si acabara
      // de llegar.
      const { isActive } = await App.getState()
      if (!isActive) return

      const permission = await LocalNotifications.checkPermissions()
      if (permission.display !== 'granted') {
        const requested = await LocalNotifications.requestPermissions()
        if (requested.display !== 'granted') return
      }

      await LocalNotifications.schedule({
        notifications: [{
          // Entero de 32 bits: Android lo exige y lo usa como identidad de la notificacion.
          id: Date.now() % 2147483647,
          title,
          body,
          channelId: CHANNEL_ID,
          smallIcon: SMALL_ICON,
          // El payload viaja aqui para que el tap pueda navegar igual que un push nativo.
          extra: notification.data || {}
        }]
      })
    },

    // Un mismo payload llega por dos vias segun donde se toco: `data` en el push nativo,
    // `extra` en la notificacion local que publica la app.
    handleNotificationTap (data = {}) {
      if (data.notification_id) {
        this.markAsRead(data.notification_id).catch(err => console.error(err))
      }
      if (data.url) {
        this.$router.replace({ path: data.url })
      }
    }
  },
  async created () {
    if (Capacitor.isNativePlatform()) {
      // El canal se crea aqui y no en el manifest porque su nombre es visible en los ajustes
      // del sistema y se traduce. Volver a crearlo con el mismo id actualiza el nombre en vez
      // de duplicar la entrada, asi que corre en cada arranque sin efecto acumulativo.
      if (Capacitor.getPlatform() === 'android') {
        try {
          await PushNotifications.createChannel({
            id: CHANNEL_ID,
            name: this.$t('notifications.channelName'),
            description: this.$t('notifications.channelDescription'),
            importance: 4,
            visibility: 1,
            lights: true,
            vibration: true
          })
        } catch (error) {
          console.error(error)
        }
      }

      const permission = await PushNotifications.requestPermissions()
      if (permission.receive === 'granted') {
        await PushNotifications.register()
      } else {
        console.error('El usuario NO tiene permisos para notificaciones')
      }

      PushNotifications.addListener('pushNotificationReceived', notification => {
        this.showForegroundNotification(notification).catch(err => console.error(err))
      })

      PushNotifications.addListener('pushNotificationActionPerformed', action => {
        this.handleNotificationTap(action.notification.data || {})
      })

      LocalNotifications.addListener('localNotificationActionPerformed', action => {
        this.handleNotificationTap(action.notification.extra || {})
      })
    }

    App.addListener('appUrlOpen', (data) => {
      const url = new URL(data.url)

      // Deep link binnacle://reset-password/:email/:id/:token (link de correo)
      if (url.protocol === 'binnacle:' && url.hostname === 'reset-password') {
        const [email, id, token] = url.pathname.split('/').filter(Boolean)
        this.$router.push({ name: 'reset-password', params: { email, id, token } }).catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
        return
      }

      const hash = url.hash.replace('#', '')
      if (hash) this.$router.replace({ path: hash })
    })
  }
}
